import React from 'react';
import { Heart, Target, Shield, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const AcercaDe = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">🧬 Acerca de Nosotros</h1>
          <p className="text-xl opacity-90">Mora BioVet Laboratorio Veterinario</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Historia */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            En Mora BioVet nacimos de una realidad que muchos médicos veterinarios y propietarios de mascotas han vivido…
            <span className="font-semibold text-[#1e3a8a]"> la necesidad de contar con resultados confiables, atención profesional y una comunicación clara.</span>
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Durante años, fuimos usuarios de distintos servicios de laboratorio. En ese proceso entendimos algo importante:
            no siempre se recibía la información completa, la orientación adecuada o la atención que realmente se necesita cuando se trata de la salud de un paciente.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            No se trataba solo de hacer estudios…
            <br />
            <span className="font-semibold text-[#1e3a8a]">👉 se trataba de entenderlos, de confiar en ellos y de sentirse respaldado.</span>
          </p>
        </div>

        {/* Por qué nace */}
        <Card className="mb-12 shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-6 flex items-center gap-3">
              <Target className="h-8 w-8 text-[#84cc16]" />
              ¿Por qué nace Mora BioVet?
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              Mora BioVet surge con un propósito claro:
            </p>
            <p className="text-xl font-semibold text-[#1e3a8a] mb-6">
              👉 Elevar el estándar del servicio en el diagnóstico veterinario.
            </p>
            <p className="text-gray-700 mb-4">Identificamos una necesidad creciente en el sector:</p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>Mayor claridad en los resultados</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>Mejor acompañamiento en la interpretación</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>Atención más humana y profesional</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>Procesos enfocados realmente en el bienestar animal</span>
              </li>
            </ul>
            <p className="text-gray-700 mt-6">
              Sin descalificar a nadie, creemos firmemente que el mercado siempre puede evolucionar.
            </p>
            <p className="text-lg font-semibold text-[#1e3a8a] mt-4">
              Por eso decidimos formar parte de ese cambio.
            </p>
          </CardContent>
        </Card>

        {/* Visión */}
        <Card className="mb-12 shadow-lg bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-6 flex items-center gap-3">
              <Users className="h-8 w-8 text-[#84cc16]" />
              Nuestra visión
            </h2>
            <p className="text-lg text-gray-700 mb-4">Queremos construir un laboratorio donde:</p>
            <ul className="space-y-3 text-gray-700 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>La precisión sea prioridad</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>La atención sea cercana y profesional</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>La información sea clara, útil y oportuna</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">•</span>
                <span>El paciente (la mascota) siempre esté en el centro</span>
              </li>
            </ul>
            <p className="text-lg font-semibold text-[#1e3a8a]">
              👉 Buscamos impulsar una competencia sana, que beneficie a todos: médicos, clientes y sobre todo, a los animales.
            </p>
          </CardContent>
        </Card>

        {/* Compromiso */}
        <Card className="mb-12 shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-6 flex items-center gap-3">
              <Heart className="h-8 w-8 text-[#84cc16]" />
              Nuestro compromiso
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              En Mora BioVet no solo realizamos análisis…
              <br />
              <span className="font-semibold text-[#1e3a8a]">👉 acompañamos decisiones clínicas importantes.</span>
            </p>
            <p className="text-gray-700 mb-4">Nos comprometemos a:</p>
            <ul className="space-y-3 text-gray-700 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">✔</span>
                <span>Entregar resultados confiables</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">✔</span>
                <span>Brindar atención respetuosa y clara</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">✔</span>
                <span>Mantener comunicación constante</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#84cc16] font-bold mt-1">✔</span>
                <span>Mejorar continuamente nuestros procesos</span>
              </li>
            </ul>
            <p className="text-lg font-semibold text-gray-700">
              Porque entendemos algo fundamental:
              <br />
              <span className="text-[#1e3a8a]">cada muestra representa una vida que importa.</span>
            </p>
          </CardContent>
        </Card>

        {/* Filosofía */}
        <Card className="mb-12 shadow-lg bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-6 flex items-center gap-3">
              <Shield className="h-8 w-8 text-[#84cc16]" />
              Nuestra filosofía
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Creemos que la medicina veterinaria no solo es técnica… también es confianza.
            </p>
            <p className="text-lg font-semibold text-[#1e3a8a] mb-4">
              👉 Y la confianza se construye con:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg text-center shadow-md">
                <p className="font-semibold text-[#1e3a8a]">Transparencia</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center shadow-md">
                <p className="font-semibold text-[#1e3a8a]">Calidad</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center shadow-md">
                <p className="font-semibold text-[#1e3a8a]">Atención</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center shadow-md">
                <p className="font-semibold text-[#1e3a8a]">Responsabilidad</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cierre */}
        <div className="text-center py-12 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] rounded-2xl text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">🐾 Mora BioVet</h2>
          <p className="text-xl mb-2">Más que un laboratorio,</p>
          <p className="text-2xl font-bold">
            👉 una nueva forma de hacer diagnóstico veterinario.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcercaDe;